import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { submitContactForm } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import SeoHead from '@/components/Seo';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await submitContactForm(formData);
      toast({
        title: "Message Sent!",
        description: "Thank you for contacting us. We'll get back to you soon.",
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast({
        title: "Message Received",
        description: "Your message has been received. We'll contact you soon.",
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: ''
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <SeoHead
        title="Contact Us | Ibadan South Diocese"
        description="Reach out to the Diocese of Ibadan South."
        ogImage="https://res.cloudinary.com/dwrqyrm84/image/upload/v1750874183/home_ss1tdy.jpg"
        url="https://ibadansouthanglicandiocese.org.ng/contact"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Contact Us
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Get in touch with us. We'd love to hear from you and help with any questions.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <Card className="border-none shadow-lg">
            <CardHeader>
              <CardTitle className="text-2xl text-purple-700">Send us a Message</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                    <Input id="name" name="name" required value={formData.name} onChange={handleChange} />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                    <Input id="email" name="email" type="email" required value={formData.email} onChange={handleChange} />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                    <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} />
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">Subject *</label>
                    <Input id="subject" name="subject" required value={formData.subject} onChange={handleChange} />
                  </div>
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">Message *</label>
                  <Textarea id="message" name="message" rows={6} required value={formData.message} onChange={handleChange} />
                </div>
                <Button type="submit" disabled={isSubmitting} className="w-full bg-purple-600 hover:bg-purple-700">
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Contact Info + Donation */}
          <div className="space-y-6">
            {/* Contact Info */}
            <Card className="border-none shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl text-purple-700">Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-gray-700">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Diocesan Office</h4>
                  <p>
                    Cathedral Church of St. David, Kudeti<br />
                    Ibadan South Diocese (Anglican Communion)<br />
                    Ibadan, Oyo State, Nigeria
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Email & Phone</h4>
                  <p>Email: info@ibadansouthanglicandiocese.org.ng</p>
                  <p>Phone: +234 xxx xxxx xxx</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Office Hours</h4>
                  <p>Mon–Fri: 8:00am – 5:00pm<br />Sat: 9:00am – 2:00pm<br />Sun: Closed (Worship)</p>
                </div>
              </CardContent>
            </Card>

            {/* Emergency Contact */}
            <Card className="border-none shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl text-purple-700">Emergency Contact</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">Pastoral Care</h4>
                  <p>If you need urgent pastoral care or hospital visits:</p>
                  <p className="text-purple-600 font-medium">+234 xxx xxxx xxx</p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">After Hours</h4>
                  <p>Please contact the duty chaplain after working hours.</p>
                </div>
              </CardContent>
            </Card>

            {/* Support Our Mission */}
            <Card className="border-none shadow-lg">
              <CardHeader>
                <CardTitle className="text-2xl text-purple-700">Support Our Mission</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-gray-700">
                <p>Your generous donations help us continue our mission of faith, outreach, and community development.</p>
                <div className="space-y-2">
                  <h4 className="font-semibold text-gray-900">Bank Transfer</h4>
                  <p>
                    <strong>Bank:</strong> First Bank Nigeria<br />
                    <strong>Account Name:</strong> Ibadan South Diocese<br />
                    <strong>Account No:</strong> 2001017784<br />
                    <strong>Type:</strong> Current
                  </p>
                  <p>
                    <strong>Bank:</strong> First Bank Nigeria<br />
                    <strong>Account Name:</strong> Ibadan South Diocese<br />
                    <strong>Account No:</strong> 3017518067<br />
                    <strong>Type:</strong> Savings
                  </p>
                </div>
                <p className="text-purple-600 font-semibold">Thank you for your generosity!</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
